import { Link } from "react-router-dom";
import {
  ArrowRight,
  Bot,
  Check,
  Gamepad2,
  Scissors,
  Twitch,
  UserRoundCheck,
  Youtube,
} from "lucide-react";
import SEO, { breadcrumbSchema } from "../Components/SEO";
import TheNav from "../Components/navbar";
import {
  streamerEditorKeywords,
  supportedTwitchGamerCategories,
} from "../utils/seoContent";

const MINT = "#10b981";
const PURPLE = "#a855f7";
const BG = "#0a0f0d";
const SURFACE = "#141a17";
const HAIRLINE = "rgba(255,255,255,0.08)";

function Section({ children, className = "" }) {
  return (
    <section className={`mx-auto w-full max-w-6xl px-6 py-20 sm:py-28 ${className}`}>
      {children}
    </section>
  );
}

function Card({ children }) {
  return (
    <div
      className="rounded-xl border p-6"
      style={{ backgroundColor: SURFACE, borderColor: HAIRLINE }}
    >
      {children}
    </div>
  );
}

function InfoCard({ icon: Icon, title, children }) {
  return (
    <Card>
      <Icon className="h-7 w-7" style={{ color: MINT }} />
      <h2 className="mt-5 text-xl font-semibold text-white">{title}</h2>
      <p className="mt-3 text-sm leading-relaxed text-zinc-400">{children}</p>
    </Card>
  );
}

function CheckItem({ children }) {
  return (
    <li className="flex items-start gap-3 text-sm leading-relaxed text-zinc-300">
      <Check className="mt-0.5 h-4 w-4 shrink-0" style={{ color: MINT }} />
      <span>{children}</span>
    </li>
  );
}

export default function StreamerEditor() {
  return (
    <>
      <SEO
        title="Streamer Editor Guide for Twitch VODs & Gaming Clips | Kre8"
        description="Learn what a streamer editor does, how Twitch VOD editing works, and when to hire a Twitch editor for stream clips, gaming highlights, Shorts, TikToks, Reels, and YouTube uploads."
        canonicalPath="/streamer-editor"
        structuredData={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Streamer Editor", path: "/streamer-editor" },
        ])}
      />
      <TheNav />
      <main style={{ backgroundColor: BG, color: "#e5e7eb" }}>
        <Section className="text-center">
          <p
            className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium uppercase tracking-wider"
            style={{ borderColor: HAIRLINE, color: MINT }}
          >
            Streamer editor guide
          </p>
          <h1 className="mx-auto mt-6 max-w-4xl text-5xl font-bold tracking-tight text-white sm:text-6xl">
            What Is a Streamer Editor for Twitch VODs and Gaming Clips?
          </h1>
          <p className="mx-auto mt-6 max-w-3xl text-lg leading-relaxed text-zinc-400 sm:text-xl">
            A streamer editor turns Twitch VODs, stream clips, gaming
            highlights, funny moments, and creator footage into YouTube videos,
            TikToks, Shorts, Reels, and recurring channel uploads.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link
              to="/gaming-video-editors"
              className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold text-[#0a0f0d]"
              style={{ backgroundColor: MINT }}
            >
              Browse Twitch editors
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/hire-video-editor-for-twitch"
              className="inline-flex items-center gap-2 rounded-full border px-6 py-3 text-sm font-bold text-white"
              style={{ borderColor: HAIRLINE }}
            >
              Hire a video editor for Twitch
            </Link>
            <Link
              to="/gaming-video-editors"
              className="inline-flex items-center gap-2 rounded-full border px-6 py-3 text-sm font-bold text-white"
              style={{ borderColor: HAIRLINE }}
            >
              Browse editors by game
            </Link>
          </div>
        </Section>

        <Section>
          <div className="grid gap-5 md:grid-cols-3">
            <InfoCard icon={Twitch} title="Twitch VOD editor">
              A Twitch VOD editor cuts down long streams, finds the best
              moments, removes slow sections, preserves context, and turns
              livestream footage into highlights or YouTube gaming videos.
            </InfoCard>
            <InfoCard icon={Scissors} title="Stream clip editor">
              A stream clip editor focuses on short-form videos, adding hooks,
              subtitles, zooms, memes, pacing, and vertical formatting for
              TikTok, Shorts, and Reels.
            </InfoCard>
            <InfoCard icon={Gamepad2} title="Gaming video editor">
              A gaming video editor understands what matters in the match:
              ranked clutches, teamfights, funny comms, creator reactions, and
              the game context viewers need.
            </InfoCard>
          </div>
        </Section>

        <Section>
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-4xl font-bold tracking-tight text-white sm:text-5xl">
              What should a streamer editor handle?
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-zinc-400">
              Streamers usually need more than simple trimming. The best edits
              turn live moments into standalone videos that make sense to
              viewers who missed the stream.
            </p>
          </div>
          <div className="mx-auto mt-12 grid max-w-4xl gap-4 md:grid-cols-2">
            <Card>
              <ul className="space-y-4">
                <CheckItem>Twitch VOD review and timestamp selection</CheckItem>
                <CheckItem>Gaming highlights, stream clips, and funny moments</CheckItem>
                <CheckItem>Captions, zooms, memes, sound cues, and pacing</CheckItem>
              </ul>
            </Card>
            <Card>
              <ul className="space-y-4">
                <CheckItem>YouTube gaming videos and recurring uploads</CheckItem>
                <CheckItem>TikTok clips, YouTube Shorts, and Instagram Reels</CheckItem>
                <CheckItem>Creator feedback, revisions, and consistent style</CheckItem>
              </ul>
            </Card>
          </div>
        </Section>

        <Section>
          <div className="grid gap-5 md:grid-cols-2">
            <InfoCard icon={Bot} title="AI clip tools are useful for speed">
              Tools can help detect moments, create quick crops, and test clips.
              They work best when the streamer already knows the moment is good
              and only needs a fast draft.
            </InfoCard>
            <InfoCard icon={UserRoundCheck} title="Human editors are better for judgment">
              Human editors can understand game context, inside jokes, tension,
              creator personality, and whether a clip deserves a TikTok, a
              YouTube Short, or a full highlight video.
            </InfoCard>
          </div>
        </Section>

        <Section>
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-4xl font-bold tracking-tight text-white sm:text-5xl">
              Stream editing categories Kre8 supports
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-zinc-400">
              Kre8 is built around Twitch gamers and creator formats, including
              game-specific editors and short-form clip workflows.
            </p>
          </div>
          <div className="mx-auto mt-10 flex max-w-5xl flex-wrap justify-center gap-3">
            {supportedTwitchGamerCategories.map((category) => (
              <span
                key={category}
                className="rounded-full border px-4 py-2 text-sm font-semibold text-zinc-200"
                style={{ borderColor: HAIRLINE, backgroundColor: SURFACE }}
              >
                {category}
              </span>
            ))}
          </div>
        </Section>

        <Section>
          <div className="mx-auto max-w-3xl text-center">
            <Youtube className="mx-auto h-8 w-8" style={{ color: MINT }} />
            <h2 className="mt-5 text-4xl font-bold tracking-tight text-white sm:text-5xl">
              Search terms this page answers
            </h2>
          </div>
          <div className="mx-auto mt-10 flex max-w-4xl flex-wrap justify-center gap-3">
            {streamerEditorKeywords.map((keyword) => (
              <span
                key={keyword}
                className="rounded-full border px-4 py-2 text-sm font-semibold text-zinc-200"
                style={{ borderColor: HAIRLINE, backgroundColor: SURFACE }}
              >
                {keyword}
              </span>
            ))}
          </div>
        </Section>
      </main>
    </>
  );
}
